import copy from "clipboard-copy";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import useClickOutside from "../hooks/useClickOutside";
import Avatar from "../parts/avatar";
import { getUserAccess } from "../utils/organization-helpers";
import { isPullActiveCoach } from "../utils/user-helpers";
import SvgIcon from "./SvgIcon";
import ToolTip from "./tooltip";

const HeaderProfile = ({
  activeUser,
  isAdmin,
  locals,
  activeUserToRequest,
  daysLeft,
}) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const [opened, setOpened] = useState(false);
  const dropdownRef = useRef(null);
  useClickOutside(
    dropdownRef,
    () => {
      setOpened(false);
    },
    opened
  );
  const userIsCoach = activeUser.permissions?.includes("coach");
  const { link: referralLink = "" } = activeUser.referralProgramInfo || {};
  const userHasCustomReferralLink = referralLink.length > 0;
  const referralProgramLinkInfo = {
    referralLinkname: userHasCustomReferralLink
      ? t("headerElements.referralProgram")
      : t("headerElements.referralProgramLink"),
    hideReferralLinkIcon: userHasCustomReferralLink,
    referralLinkUrl: !userHasCustomReferralLink
      ? "https://mystrengthbook.firstpromoter.com/"
      : "/actions/refresh?modal[type]=referral-link-program",
  };
  const { referralLinkname, hideReferralLinkIcon, referralLinkUrl } =
    referralProgramLinkInfo;
  const isCoachingPortal = Boolean(
    locals?.pathname?.includes("coaching-portal")
  );
  const userIsPressCoach =
    (!activeUser.organizationId && !locals.organizationOwner) || !userIsCoach
      ? Boolean(activeUser.pressAccountInfo)
      : Boolean(locals.organizationOwner.pressAccountInfo);

  const userIsPushCoach = getUserAccess(
    activeUser,
    locals.organizationOwner,
    locals.organizationPermissions,
    locals.organizationRoles
  ).userCanAccessToPush;

  const showPullButton = userIsCoach && !isPullActiveCoach(activeUser);
  const showPushButton = userIsCoach && !userIsPushCoach;
  const showPressButton = userIsCoach && !userIsPressCoach;

  const pushSubdomain = activeUserToRequest?.pushAccountInfo?.subdomain;
  const showPushLink = userIsPushCoach && Boolean(pushSubdomain);
  const showPressLink = userIsPressCoach && Boolean(pushSubdomain);
  const hasStorefrontLinks = showPushLink || showPressLink;
  const pushStorefrontUrl = `https://${pushSubdomain}.programs.app`;
  const pressStorefrontUrl = `https://${pushSubdomain}.gymmembership.app`;
  const [copyTooltip, setCopyTooltip] = useState({
    push: "copyToClipboard",
    press: "copyToClipboard",
  });
  const handleCopyLink = async (type, url) => {
    await copy(url);
    setCopyTooltip((prev) => ({ ...prev, [type]: "copied" }));
    setTimeout(() => {
      setCopyTooltip((prev) => ({ ...prev, [type]: "copyToClipboard" }));
    }, 30000);
  };
  const isPullCoachOnTrial =
    activeUserToRequest.coachingFreeTrial && daysLeft > 0;
  const isPushCoachWithEndedTrial =
    activeUserToRequest.permissions?.includes("coach") &&
    activeUserToRequest.pushAccountInfo &&
    daysLeft < 1 &&
    !isPullActiveCoach(activeUserToRequest);
  useEffect(() => {
    //get details element
    const details = document.getElementById("profile-dropdown");
    //check if open attribute is not present
    if (!details.hasAttribute("open")) {
      //set open attribute to true
      details.setAttribute("open", true);
    }
  }, []);
  return (
    <>
      <details open={true} ref={dropdownRef} id="profile-dropdown" role="list">
        <summary
          aria-haspopup="listbox"
          onClick={(e) => {
            e.preventDefault();
            setOpened(!opened);
          }}
        >
          <Avatar size={"2.5"} unit="rem" />
          <div className="mobile-hidden">{activeUser.name}</div>
          <SvgIcon src={`/img/svg/angle-${opened ? "up" : "down"}.svg`} />
        </summary>
        <div className={opened ? "opened" : undefined}>
          <fieldset>
            <legend>{t("headerElements.account")}</legend>
            <ul>
              <li>
                <a
                  onClick={() => setOpened(false)}
                  href={
                    userIsCoach
                      ? "/coaching-portal/settings/profile"
                      : "/dashboard/settings/profile"
                  }
                  role="button"
                  data-button-type="text-white"
                  data-icon
                >
                  <SvgIcon src="/img/new-icons/profile-circle.svg" />
                  {t("headerElements.editProfile")}
                </a>
              </li>
              {userIsCoach && (
                <>
                  <li>
                    <a
                      onClick={() => setOpened(false)}
                      href="/coaching-portal/settings/manage-coaches?organizationInviteCoach=true"
                      role="button"
                      data-button-type="text-white"
                      data-icon
                    >
                      <SvgIcon src="/img/new-icons/profile-circle.svg" />
                      {t("headerElements.inviteNewCoach")}
                    </a>
                  </li>
                  {isCoachingPortal && (
                    <li>
                      <a
                        className="anchor-with-two-icons"
                        onClick={() => setOpened(false)}
                        role="button"
                        data-button-type="text-white"
                        data-icon
                        href={referralLinkUrl}
                        target={
                          !userHasCustomReferralLink ? "_blank" : undefined
                        }
                        rel={
                          !userHasCustomReferralLink
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        <div className="header-element-icon-text-icon">
                          <SvgIcon src="/img/new-icons/vuesax-linear-share.svg" />
                          <p>{referralLinkname}</p>
                        </div>
                        {!hideReferralLinkIcon && (
                          <SvgIcon src="/img/new-icons/export.svg" />
                        )}
                      </a>
                    </li>
                  )}
                </>
              )}
            </ul>
          </fieldset>
          {userIsCoach && (
            <>
              <fieldset>
                <legend>{t("headerElements.billing")}</legend>
                {isPullCoachOnTrial || isPushCoachWithEndedTrial ? (
                  <a
                    href="/coach-billing"
                    role="button"
                    data-button-type="secondary"
                    className="my-3"
                  >
                    <span className="clipped font-semibold">
                      {t("common:upgradeNow")}
                    </span>
                  </a>
                ) : (
                  <ul>
                    <li>
                      <a
                        onClick={() => setOpened(false)}
                        href="/coaching-portal/settings/coach-manage-billing"
                        role="button"
                        data-button-type="text-white"
                        data-icon
                      >
                        <SvgIcon src="/img/new-icons/card.svg" />
                        {t("headerElements.manageBilling")}
                      </a>
                    </li>
                    <li>
                      <a
                        onClick={() => setOpened(false)}
                        href="/coaching-portal/settings/coach-billing"
                        role="button"
                        data-button-type="text-white"
                        data-icon
                      >
                        <SvgIcon src="/img/new-icons/profile-circle.svg" />
                        {t("headerElements.coachBilling")}
                      </a>
                    </li>
                  </ul>
                )}
              </fieldset>
              {hasStorefrontLinks && (
                <fieldset>
                  <legend>{t("headerElements.links")}</legend>
                  <ul>
                    {showPushLink && (
                      <li className="storefront-link flex flex-col justify-start! items-stretch! gap-2 px-2! py-2!">
                        <div className="flex justify-between items-center w-full">
                          <p className="p1">
                            {t("store.storefrontLinks.pushStorefront")}
                          </p>
                          <a
                            href={pushStorefrontUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <SvgIcon src="/img/new-icons/export.svg" />
                          </a>
                        </div>
                        <ToolTip
                          id="copy-push-subdomain"
                          body={t(`store.${copyTooltip.push}`)}
                          hintClassNames="img-copy-store"
                        >
                          <button
                            type="button"
                            className="chip flex gap-2 items-center w-full! px-1! py-1! text-(--information) bg-(--information-opacity) cursor-pointer"
                            onClick={() =>
                              handleCopyLink("push", pushStorefrontUrl)
                            }
                          >
                            <SvgIcon
                              src="/img/new-icons/link-white.svg"
                              classNames="shrink-0"
                            />
                            <span className="truncate min-w-0 flex-1 text-left">{`${pushSubdomain}.programs.app`}</span>
                          </button>
                        </ToolTip>
                      </li>
                    )}
                    {showPressLink && (
                      <li className="storefront-link flex flex-col justify-start! items-stretch! gap-2 px-2! py-2!">
                        <div className="flex justify-between items-center w-full">
                          <p className="p1">
                            {t("store.storefrontLinks.pressStorefront")}
                          </p>
                          <a
                            href={pressStorefrontUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <SvgIcon src="/img/new-icons/export.svg" />
                          </a>
                        </div>
                        <ToolTip
                          id="copy-press-subdomain"
                          body={t(`store.${copyTooltip.press}`)}
                          hintClassNames="img-copy-store"
                        >
                          <button
                            type="button"
                            className="chip flex gap-2 items-center w-full! px-1! py-1! text-(--information) bg-(--information-opacity) cursor-pointer"
                            onClick={() =>
                              handleCopyLink("press", pressStorefrontUrl)
                            }
                          >
                            <SvgIcon
                              src="/img/new-icons/link-white.svg"
                              classNames="shrink-0"
                            />
                            <span className="truncate min-w-0 flex-1 text-left">{`${pushSubdomain}.gymmembership.app`}</span>
                          </button>
                        </ToolTip>
                      </li>
                    )}
                  </ul>
                </fieldset>
              )}
              <fieldset>
                <legend>{t("headerElements.products")}</legend>
                <ul>
                  <li>
                    <a
                      onClick={() => setOpened(false)}
                      role="button"
                      data-button-type="text-white"
                      data-icon
                      href="https://mystrengthbook.canny.io/feature-requests"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <p>{t("headerElements.featureRequest")}</p>
                      <SvgIcon src="/img/new-icons/export.svg" />
                    </a>
                  </li>
                  {showPullButton && (
                    <li>
                      <a
                        onClick={() => setOpened(false)}
                        role="button"
                        data-button-type="text-white"
                        data-icon
                        href="https://mystrengthbook.com/pull/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <p>{t("headerElements.upgradeToPullCoaching")}</p>

                        <SvgIcon src="/img/new-icons/export.svg" />
                      </a>
                    </li>
                  )}
                  {showPushButton && (
                    <li>
                      <a
                        onClick={() => setOpened(false)}
                        role="button"
                        data-button-type="text-white"
                        data-icon
                        href="https://form.typeform.com/to/jOPku6ra"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <p>
                          {t("headerElements.requestPushAccessSubscriptions")}
                        </p>
                        <SvgIcon src="/img/new-icons/export.svg" />
                      </a>
                    </li>
                  )}
                  {showPressButton && (
                    <li>
                      <a
                        onClick={() => setOpened(false)}
                        role="button"
                        data-button-type="text-white"
                        data-icon
                        href="https://form.typeform.com/to/jOPku6ra"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <p>
                          {t("headerElements.requestPressAccessGymManagement")}
                        </p>
                        <SvgIcon src="/img/new-icons/export.svg" />
                      </a>
                    </li>
                  )}
                </ul>
              </fieldset>
            </>
          )}

          {isAdmin && (
            <fieldset>
              <legend>{t("headerElements.admin")}</legend>
              <ul>
                <li>
                  <a
                    onClick={() => setOpened(false)}
                    role="button"
                    data-button-type="text-white"
                    data-icon
                    href="/admin/users"
                  >
                    <SvgIcon src="/img/new-icons/layer-group.svg" />
                    {t("headerElements.console")}
                  </a>
                </li>
              </ul>
            </fieldset>
          )}
          <fieldset>
            <legend>{t("headerElements.support")}</legend>
            <ul>
              <li>
                <a
                  onClick={() => setOpened(false)}
                  role="button"
                  data-button-type="text-white"
                  data-icon
                  href={
                    userIsCoach
                      ? "/coaching-portal/support/contact"
                      : "/dashboard/support/contact"
                  }
                >
                  <SvgIcon src="/img/new-icons/call.svg" />
                  {t("headerElements.contactUs")}
                </a>
              </li>
            </ul>
          </fieldset>
          <a role="button" href="/logout">
            {t("headerElements.logout")}
          </a>
        </div>
      </details>
    </>
  );
};

const onSubmit = (e) => {
  e.preventDefault();
  submit(`/actions/refresh`, {
    method: "GET",
    body: { name: e.target.name.value },
  });
};

const AthleteFinder = ({ handleShowFinder = () => {}, mobile = false }) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  return (
    <form onSubmit={onSubmit}>
      <input role="submit-athlete-search" type="submit" value="" />
      <input
        type="search"
        name="name"
        placeholder={t("headerElements.searchPlaceholder")}
        defaultValue={""}
      />
      {mobile && (
        <button onClick={handleShowFinder}>
          <img src="/img/svg/close.svg" alt={t("common:close")} />
        </button>
      )}
    </form>
  );
};

export { HeaderProfile, AthleteFinder };
